import { useEffect } from 'react'

/**
 * Track the soft-keyboard inset via the Visual Viewport API and expose it
 * as a `--kbd-inset` CSS custom property on `<html>`.
 *
 * Why this exists: Android's `windowSoftInputMode="adjustResize"` does NOT
 * shrink the WebView's layout viewport (`window.innerHeight`) — only the
 * visual viewport reports the keyboard inset. Capacitor's
 * `KeyboardResize.Native` shrinks the WebView on iOS but the corresponding
 * Android path is a no-op in the current plugin version. So CSS units like
 * `svh` / `vh` / `dvh` stay at the unresized height on Android, which lets
 * `items-end`-aligned dialog content slide right behind the keyboard.
 *
 * Fix: subscribe to `visualViewport.resize` (fires on keyboard show/hide),
 * compute `innerHeight - visualViewport.height - visualViewport.offsetTop`,
 * write to `--kbd-inset`. Consumers add `pb-[var(--kbd-inset,0px)]` to the
 * outer fixed flex container so `items-end` lands above the keyboard, and
 * cap dialog height with `calc(100svh - var(--kbd-inset,0px))` so the
 * dialog body shrinks to fit.
 *
 * `enabled` gates registration so the hook can be mounted in a component
 * that's always present (e.g., `IssueDialog` lives in the issues route
 * regardless of dialog open state) but only runs when the relevant dialog
 * is actually visible. Cleanup removes the CSS var so other dialogs don't
 * inherit a stale inset after this one closes.
 */
export function useKeyboardInsetCssVar(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return
    const vv = window.visualViewport
    if (!vv) return
    const root = document.documentElement

    const apply = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      root.style.setProperty('--kbd-inset', `${inset}px`)
    }

    apply()
    vv.addEventListener('resize', apply)
    vv.addEventListener('scroll', apply)
    return () => {
      vv.removeEventListener('resize', apply)
      vv.removeEventListener('scroll', apply)
      root.style.removeProperty('--kbd-inset')
    }
  }, [enabled])
}
